import { Autocomplete, Chip, CircularProgress, TextField, TextFieldProps } from '@mui/material';
import { QueryKey } from '@tanstack/react-query';
import { unionBy } from 'lodash-es';
import { SyntheticEvent, useRef } from 'react';
import { Control, Controller, FieldValues, Path, useController } from 'react-hook-form';
import useDebouncedSearch from '../../hooks/useDebouncedSearch';
import { ApiService } from '../../utils/api';

export type AutocompleteItem = {
  id: string;
  userId?: string;
  name?: string;
};

export type FormAutocompleteProps<
  O extends AutocompleteItem,
  TField extends FieldValues,
  TTransformedValues extends FieldValues | undefined = TField,
> = Omit<TextFieldProps, 'error'> & {
  control: Control<TField, unknown, TTransformedValues>;
  name: Path<TField>;
  url: string;
  apiService?: ApiService;
  queryKey: QueryKey;
  queryParams?: Record<string, unknown>;
  initialOptions?: O[];
  placeholder?: string;
  required?: boolean;
  label?: string;
  error?: {
    message?: string;
  };
  disabled?: boolean;
  hookEnabled?: boolean;
  multiple?: boolean;
  getOptionLabel: (options: O[], value: string) => string;
  optionsFilter?: 'userId' | 'id' | 'name';
  isOptionAvailable?: (option: O, selectedValues: string[]) => boolean;
  onSelectionChange?: (selectedItems: O[]) => void;
  remoteErrorText?: string;
};

const getSelectedValues = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  if (typeof value === 'string') return [value];

  return [];
};

const FormAutocomplete = <
  O extends AutocompleteItem,
  TField extends FieldValues,
  TTransformedValues extends FieldValues | undefined = TField,
>({
  control,
  name,
  url,
  apiService,
  queryKey,
  queryParams,
  initialOptions = [],
  placeholder,
  required,
  label,
  error,
  disabled,
  hookEnabled,
  multiple = false,
  getOptionLabel,
  optionsFilter = 'id',
  isOptionAvailable,
  onSelectionChange,
  remoteErrorText = 'Unable to load options.',
  slotProps,
  ...rest
}: FormAutocompleteProps<O, TField, TTransformedValues>) => {
  const { data, search, isError, isFetching, onSearch } = useDebouncedSearch<O>(
    apiService,
    url,
    queryKey,
    {
      additionalParams: queryParams,
    },
    hookEnabled
  );

  const { field } = useController({ name, control });

  // Server-side search only returns the current page, so cache selected objects for labels/dependent filters.
  const selectedItemsRef = useRef<O[]>([]);

  const remoteOptions = unionBy(data?.selectedItems, data?.items, 'id');
  const selectedValues = getSelectedValues(field.value);
  const cachedOptions = unionBy(remoteOptions, initialOptions, selectedItemsRef.current, 'id');
  const options = isOptionAvailable
    ? cachedOptions.filter((option) => isOptionAvailable(option, selectedValues))
    : cachedOptions;

  const autocompleteOptions = options?.reduce((acc: string[], item) => {
    const optionValue = item[optionsFilter];

    if (optionValue) {
      acc.push(optionValue);
    }

    return acc;
  }, []);

  const handleChange = (_: React.SyntheticEvent<Element, Event>, newValue: string[] | string | null) => {
    field.onChange(newValue);
    const selectedValues = getSelectedValues(newValue);
    const selectedItems = options?.filter((item) => {
      const optionValue = item[optionsFilter];

      return optionValue ? selectedValues.includes(optionValue) : false;
    });

    selectedItemsRef.current = unionBy(selectedItems, selectedItemsRef.current, 'id');
    onSelectionChange?.(selectedItems ?? []);
    onSearch('');
  };

  const handleInputChange = (_event: SyntheticEvent<Element, Event>, value: string, reason: string) => {
    if (reason === 'input') {
      onSearch(value);
    }
  };

  const autocompleteValue = field.value ?? (multiple ? [] : null);
  const helperText = error?.message ?? (isError ? remoteErrorText : undefined);

  return (
    <Controller
      control={control}
      name={name}
      render={() => {
        return (
          <Autocomplete
            id='asynchronous-autocomplete'
            multiple={multiple}
            value={autocompleteValue}
            options={autocompleteOptions || []}
            onChange={handleChange}
            getOptionKey={(option) => option}
            getOptionLabel={(id) => {
              return getOptionLabel(options || [], id);
            }}
            renderValue={(value, getItemProps) =>
              (Array.isArray(value) ? value : [value]).map((option, index) => {
                if (!autocompleteOptions.includes(option)) {
                  return null;
                }

                return <Chip label={getOptionLabel(options || [], option)} {...getItemProps({ index })} key={option} />;
              })
            }
            filterOptions={(x) => x}
            clearOnBlur={false}
            loading={isFetching}
            disabled={disabled}
            onInputChange={handleInputChange}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  value={search || ''}
                  label={label}
                  placeholder={placeholder}
                  error={!!error || isError}
                  required={required}
                  helperText={helperText}
                  slotProps={{
                    ...params.slotProps,
                    ...slotProps,
                    input: {
                      ...params.slotProps.input,
                      ...slotProps?.input,
                      endAdornment: (
                        <>
                          {isFetching ? <CircularProgress color='inherit' size={20} /> : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      ),
                    },
                    inputLabel: slotProps?.inputLabel,
                  }}
                />
              );
            }}
          />
        );
      }}
    />
  );
};

export default FormAutocomplete;
